import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail } from "lucide-react";
import { api } from "../api";
import { AuthLayout } from "../components/AuthLayout";

function isEmailShaped(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const emailInvalid = touched && email.length > 0 && !isEmailShaped(email);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!isEmailShaped(email)) {
      setError("Enter a valid email address to continue.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title={sent ? "Check your inbox" : "Reset your password"}
      subtitle={sent
        ? "We just sent a reset link if the email matches an account."
        : "We'll email you a link to pick a new password."}
    >
      {sent ? (
        <div className="card">
          <div className="card-body text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm text-ink">
              If <strong>{email}</strong> matches an account, a reset link is on its way. It expires in 1 hour.
            </p>
            <p className="text-xs text-slate-500">
              Didn't get it? Check your spam folder, then try again in a minute.
            </p>
            <Link to="/login" className="btn-secondary w-full">
              Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="card" noValidate>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="fp-email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="fp-email"
                type="email"
                className={`input ${emailInvalid ? "!ring-rose-400 !border-rose-400" : ""}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                autoFocus
                autoComplete="username"
                placeholder="you@example.com"
                aria-invalid={emailInvalid}
                aria-describedby={emailInvalid ? "fp-email-err" : undefined}
                required
              />
              {emailInvalid && (
                <p id="fp-email-err" className="mt-1 text-xs text-rose-700">
                  That doesn't look like an email address.
                </p>
              )}
            </div>
            {error && (
              <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
                {error}
              </div>
            )}
            <button type="submit" className="btn-primary w-full" disabled={submitting}>
              {submitting ? (
                <><span className="spinner" aria-hidden="true" /> Sending link…</>
              ) : (
                <><Mail className="h-4 w-4" aria-hidden="true" /> Send reset link</>
              )}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-brand-600 hover:text-brand-700 hover:underline">
                Back to sign in
              </Link>
            </div>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
